import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, Loader2, X, Building2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  USERNAME_MAX,
  USERNAME_MIN,
  checkUsernameAvailable,
  claimBrandHandle,
  renameBrandHandle,
  fetchBrandHandleStatus,
  validateUsernameFormat,
  type BrandHandleStatus,
} from "@/lib/username";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace, type Workspace } from "@/hooks/useWorkspace";

type Status = "idle" | "checking" | "available" | "taken" | "invalid";

type Props = {
  workspace: Workspace;
};

/**
 * Per-workspace brand handle. Lives in the same global namespace as personal
 * usernames; same format, same 365-day rename cooldown, same 90-day former-
 * handle reservation. Optional — workspaces work fine without one.
 *
 * Only the workspace owner can claim or rename. Members/admins see a
 * read-only view with a tooltip explanation.
 */
export const WorkspaceBrandHandleCard = ({ workspace }: Props) => {
  const { user } = useAuth();
  const { refresh } = useWorkspace();

  const isOwner = !!user && workspace.owner_user_id === user.id;
  const isFirstClaim = !workspace.brand_handle;
  const workspaceNamed = !!workspace.workspace_label?.trim();

  const [info, setInfo] = useState<BrandHandleStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await fetchBrandHandleStatus(workspace.id);
    setInfo(r);
    setLoading(false);
  }, [workspace.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const validation = useMemo(() => validateUsernameFormat(value), [value]);
  const debounceRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (!validation.ok) {
      setStatus(value.length === 0 ? "idle" : "invalid");
      setErrorMessage(
        value.length === 0
          ? null
          : (validation as { ok: false; message: string }).message,
      );
      return;
    }
    if (info?.brandHandle && validation.normalized === info.brandHandle) {
      setStatus("invalid");
      setErrorMessage("That's already this workspace's handle.");
      return;
    }
    setStatus("checking");
    setErrorMessage(null);
    debounceRef.current = window.setTimeout(async () => {
      const ok = await checkUsernameAvailable(validation.normalized);
      setStatus(ok ? "available" : "taken");
      if (!ok) setErrorMessage("That handle is taken or reserved.");
    }, 350);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [value, validation, open, info?.brandHandle]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status !== "available" || !validation.ok) return;
    setSubmitting(true);
    try {
      if (isFirstClaim) {
        await claimBrandHandle(workspace.id, validation.normalized);
        toast.success(`Brand handle @${validation.normalized} claimed`);
      } else {
        await renameBrandHandle(workspace.id, validation.normalized);
        toast.success(`Renamed to @${validation.normalized}`);
      }
      setOpen(false);
      setValue("");
      setStatus("idle");
      await Promise.all([load(), refresh()]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Save failed.";
      setErrorMessage(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const renameDisabledReason = !isOwner
    ? "Only the workspace owner can change its brand handle."
    : !info?.canRenameNow && info?.nextEligibleAt
      ? `You can rename again on ${info.nextEligibleAt.toLocaleDateString()}.`
      : undefined;

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-4 w-4" /> Workspace brand handle
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-muted-foreground">
          Your personal handle is your <strong>account</strong> identity. Each workspace can also
          have its own <span className="font-mono">@brand_handle</span> for publishing and
          brand-facing pages. It lives in the same global namespace as personal handles and
          follows the same once-a-year rename rule.
        </p>

        {loading ? (
          <p className="text-muted-foreground">Loading…</p>
        ) : !info?.brandHandle ? (
          <div className="flex items-center justify-between gap-3">
            <p className="text-muted-foreground">
              {workspaceNamed
                ? "No brand handle yet for this workspace."
                : "Name this workspace first to claim its brand handle."}
            </p>
            <Button
              size="sm"
              disabled={!isOwner || !workspaceNamed}
              title={
                !isOwner
                  ? "Only the workspace owner can claim a brand handle."
                  : !workspaceNamed
                    ? "Give this workspace a name above before claiming a brand handle."
                    : undefined
              }
              onClick={() => {
                setValue("");
                setStatus("idle");
                setErrorMessage(null);
                setOpen(true);
              }}
            >
              Claim handle
            </Button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3">
              <div className="font-mono text-base font-medium">
                @{info.brandHandle}
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={!isOwner || !info.canRenameNow}
                title={renameDisabledReason ?? undefined}
                onClick={() => {
                  setValue("");
                  setStatus("idle");
                  setErrorMessage(null);
                  setOpen(true);
                }}
              >
                Rename
              </Button>
            </div>
            {!info.canRenameNow && info.nextEligibleAt && (
              <p className="rounded-md border border-border bg-muted/30 p-2 text-xs text-muted-foreground">
                You can rename this brand handle again on{" "}
                <span className="text-foreground">
                  {info.nextEligibleAt.toLocaleDateString()}
                </span>
                . Renames are limited to once every 12 months.
              </p>
            )}
          </>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={(o) => !submitting && setOpen(o)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isFirstClaim ? "Claim a brand handle" : "Rename brand handle"}
            </DialogTitle>
            <DialogDescription>
              {isFirstClaim ? (
                <>
                  Pick a handle for this workspace. You won't be able to change it again for{" "}
                  <span className="font-medium text-foreground">12 months</span>.
                </>
              ) : (
                <>
                  Choose carefully — you won't be able to change this again for{" "}
                  <span className="font-medium text-foreground">12 months</span>.
                  The current handle (<span className="font-mono">@{info?.brandHandle}</span>)
                  will be reserved for 90 days after the change to prevent impersonation, then released.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={onSubmit} className="space-y-3" noValidate>
            <div className="space-y-2">
              <Label htmlFor={`brand-handle-${workspace.id}`}>
                {isFirstClaim ? "Brand handle" : "New brand handle"}
              </Label>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-muted-foreground">
                  @
                </span>
                <Input
                  id={`brand-handle-${workspace.id}`}
                  autoFocus
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  value={value}
                  onChange={(e) =>
                    setValue(e.target.value.toLowerCase().slice(0, USERNAME_MAX))
                  }
                  className="pl-7 pr-9"
                  placeholder="yourbrand"
                  disabled={submitting}
                  aria-invalid={status === "invalid" || status === "taken"}
                />
                <span className="absolute inset-y-0 right-3 flex items-center">
                  {status === "checking" ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  ) : status === "available" ? (
                    <Check className="h-4 w-4 text-primary" />
                  ) : status === "taken" || status === "invalid" ? (
                    <X className="h-4 w-4 text-destructive" />
                  ) : null}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {USERNAME_MIN}–{USERNAME_MAX} lowercase letters and numbers. Must include at least one letter.
              </p>
              {errorMessage ? (
                <p className="text-sm text-destructive">{errorMessage}</p>
              ) : status === "available" ? (
                <p className="text-sm text-primary">
                  @{validation.ok ? validation.normalized : ""} is available.
                </p>
              ) : null}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setOpen(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={status !== "available" || submitting}
              >
                {submitting
                  ? isFirstClaim
                    ? "Claiming…"
                    : "Renaming…"
                  : isFirstClaim
                    ? "Claim handle"
                    : "Rename handle"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
};


